import type { ComponentPropsWithoutRef } from 'react';
import { PreviewCard as BasePreviewCard } from '@base-ui/react/preview-card';
import { merge } from 'lodash-es';
import React from 'react';

import type { GutterProp } from '../../utilities/shared/Gutter';
import type { MaxWidthProps } from '../../utilities/shared/MaxWidth';
import type { MinWidthProps } from '../../utilities/shared/MinWidth';
import type { SpacingScale } from '../../utilities/shared/sizes';
import type { WidthProps } from '../../utilities/shared/Width';
import { zIndexes } from '../../common/z_indexes';
import { colors, darkThemeSelector, keyframes, shadows, styled } from '../../stitches.config';
import { gutterCSS } from '../../utilities/shared/Gutter';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { spacingCSS } from '../../utilities/shared/Spacing';
import { widthCSS } from '../../utilities/shared/Width';

export const createPreviewCardHandle = BasePreviewCard.createHandle;

export type PreviewCardHandle<T = unknown> = ReturnType<typeof BasePreviewCard.createHandle<T>>;

/** Content entering when switching shared-handle triggers (see Viewport `data-current`). */
const previewCardViewportEnter = keyframes({
  from: { opacity: 0, transform: 'translateY(6px)' },
  to: { opacity: 1, transform: 'translateY(0)' },
});

/** Content exiting when switching shared-handle triggers (see Viewport `data-previous`). */
const previewCardViewportExit = keyframes({
  from: { opacity: 1 },
  to: { opacity: 0 },
});

const PreviewCardPopover = styled(BasePreviewCard.Popup, {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  background: colors.bgApplicationLight,
  borderRadius: '$10',
  boxShadow: shadows.overlayLight,

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    boxShadow: shadows.overlayDark,
  },

  '@media (prefers-reduced-motion: no-preference)': {
    transition:
      'opacity 150ms ease-out, transform 150ms ease-out, width 220ms cubic-bezier(0.2, 0, 0, 1), height 220ms cubic-bezier(0.2, 0, 0, 1)',

    '&[data-starting-style]': {
      opacity: 0,
      transform: 'translateY(4px) scale(0.98)',
    },

    '&[data-ending-style]': {
      opacity: 0,
      transform: 'translateY(4px) scale(0.98)',
    },
  },
});
PreviewCardPopover.displayName = 'PreviewCardPopover';

const PreviewCardPositioner = styled(BasePreviewCard.Positioner, {
  zIndex: zIndexes.overlay,

  /**
   * Smoothly move the popup when the anchor changes (multiple triggers sharing one handle).
   * @see https://base-ui.com/react/components/preview-card#animating-the-preview-card
   */
  '@media (prefers-reduced-motion: no-preference)': {
    transitionProperty: 'top, right, bottom, left, transform',
    transitionDuration: '220ms',
    transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
  },
});
PreviewCardPositioner.displayName = 'PreviewCardPositioner';

const PreviewCardPortal = styled(BasePreviewCard.Portal);
PreviewCardPortal.displayName = 'PreviewCardPortal';

export type PreviewCardViewportProps = ComponentPropsWithoutRef<typeof BasePreviewCard.Viewport>;

export const PreviewCardViewport = styled(BasePreviewCard.Viewport, {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',

  '@media (prefers-reduced-motion: no-preference)': {
    '& [data-current]': {
      animation: `${previewCardViewportEnter} 240ms cubic-bezier(0.2, 0, 0, 1) both`,
    },
    '& [data-previous]': {
      animation: `${previewCardViewportExit} 200ms cubic-bezier(0.4, 0, 1, 1) both`,
    },
  },
});
PreviewCardViewport.displayName = 'PreviewCardViewport';

export type PreviewCardContentProps = {
  /**
   * How to align the popup relative to the trigger. Defaults to `'center'`.
   */
  alignment?: 'start' | 'center' | 'end';
  /**
   * The content to display inside the preview card popup.
   */
  children: React.ReactNode;
  /**
   * The amount of padding to add to the popup to prevent it from being cut off by the viewport. Defaults to `12`.
   */
  collisionPadding?: SpacingScale;
  /**
   * The amount of spacing to add between the content and the popup. Defaults to `12`.
   */
  gap?: SpacingScale;
  /**
   * Which side of the trigger to display the popup. Defaults to `'bottom'`.
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Distance between the trigger and the popup in pixels. Defaults to `8`.
   */
  sideOffset?: SpacingScale;
  /**
   * When true, wraps children in `PreviewCard.Viewport` so content can animate when switching
   * between multiple triggers that share one `PreviewCard` via `createPreviewCardHandle` and `payload`.
   */
  viewport?: boolean;
} & GutterProp &
  WidthProps &
  MaxWidthProps &
  MinWidthProps &
  GutterProp;

export function PreviewCardContent({
  alignment = 'center',
  children,
  collisionPadding = 12,
  gap = 12,
  gutter = { context: 'content', edge: 'all' },
  side = 'bottom',
  sideOffset = 8,
  viewport = false,
  width,
  maxWidth = 360,
  minWidth = 240,
}: PreviewCardContentProps) {
  const previewCardPopoverCSS = merge(
    {
      maxWidth: 'var(--available-width)',
      maxHeight: 'var(--available-height)',
    },
    spacingCSS(gap),
    gutterCSS(gutter),
    widthCSS(width),
    maxWidth ? maxWidthCSS(maxWidth) : undefined,
    minWidthCSS(minWidth),
  );

  const inner = viewport ? <PreviewCardViewport>{children}</PreviewCardViewport> : children;

  return (
    <PreviewCardPortal>
      <PreviewCardPositioner
        side={side}
        sideOffset={sideOffset}
        align={alignment}
        collisionPadding={collisionPadding}
      >
        <PreviewCardPopover css={previewCardPopoverCSS}>{inner}</PreviewCardPopover>
      </PreviewCardPositioner>
    </PreviewCardPortal>
  );
}

export type PreviewCardTriggerProps = ComponentPropsWithoutRef<typeof BasePreviewCard.Trigger>;

export const PreviewCardTriggerContainer = styled(BasePreviewCard.Trigger);
PreviewCardTriggerContainer.displayName = 'PreviewCardTriggerContainer';

export function PreviewCardTrigger({
  closeDelay = 600,
  delay = 100,
  ...props
}: PreviewCardTriggerProps) {
  return <PreviewCardTriggerContainer closeDelay={closeDelay} delay={delay} {...props} />;
}

export type PreviewCardProps = ComponentPropsWithoutRef<typeof BasePreviewCard.Root>;

export const PreviewCard = styled(BasePreviewCard.Root);
PreviewCard.displayName = 'PreviewCard';
